import React, { useState } from 'react';
import { format } from 'date-fns';
import Container from '@mui/material/Container';
import PermissionBasedGuard from 'src/auth/guard/permission-based-guard';
import { useAuthContext } from 'src/auth/hooks';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs/custom-breadcrumbs';
import { useGetSelectedFoods, useGetSelectedFoodDetails, useGetRestaurantSelectedFoods } from 'src/api/restaurant';
import {
  TableCell,
  TableRow,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Grid,
  Divider,
  Collapse
} from '@mui/material';
import { components } from 'src/types/schema';
import Iconify from 'src/components/iconify';
import { DataTable, TableToolbar, useFilters, usePagination, Column } from './selected-food-table';

// ----------------------------------------------------------------------

type SelectedFood = components['schemas']['PartnersSubscriptionSelectedFoodFoodsData'];
type FoodGroup = components['schemas']['PartnersSubscriptionSelectedFoodFoodsList'];
type FoodDetail = components['schemas']['PartnersSubscriptionsSelectedFoodFoodDetails'];

// ----------------------------------------------------------------------

export default function SelectedFoodListPage({ restaurantId }: { restaurantId?: string }) {
  const { user } = useAuthContext();
  const { filters, onFilters, onMultipleFilters, resetFilters, getDateRange } = useFilters('selected_foods');
  const { page, rowsPerPage, onPageChange, onRowsPerPageChange } = usePagination();
  const [selectedFoodDetails, setSelectedFoodDetails] = useState<{ id: number, date: string } | null>(null);
  const [expandedSubscriptions, setExpandedSubscriptions] = useState<Record<number, boolean>>({});

  const dateRange = getDateRange(filters.selectedDay);

  // Use restaurant-specific API if restaurantId is provided, otherwise use general API
  const { selectedFoods, selectedFoodsLoading, selectedFoodsError } = restaurantId 
    ? useGetRestaurantSelectedFoods(
        restaurantId,
        filters.search || undefined,
        dateRange.date_gte,
        dateRange.date_lte
      )
    : useGetSelectedFoods(
        filters.search || undefined,
        dateRange.date_gte,
        dateRange.date_lte
      );

  const { selectedFood, selectedFoodLoading, selectedFoodError } = useGetSelectedFoodDetails(
    selectedFoodDetails?.id || 0,
    selectedFoodDetails?.date || ''
  );

  // Handle closing the food details dialog
  const handleCloseDetails = () => {
    setSelectedFoodDetails(null);
    setExpandedSubscriptions({}); // Reset expanded state when closing dialog
  };

  // Toggle subscription expanded state
  const toggleSubscription = (index: number) => {
    setExpandedSubscriptions((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Prepare data for display
  const prepareData = () => {
    if (!selectedFoods) return [];

    // Flatten the data for client-side pagination
    const flattenedData = selectedFoods.reduce((acc: Array<{ group: FoodGroup; food: SelectedFood }>, group) => {
      group.data.forEach(food => {
        acc.push({ group, food });
      });
      return acc;
    }, []);

    const paginatedData = flattenedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    // Group the paginated data back by date for display
    const groupedData = paginatedData.reduce((acc: { [key: string]: { food: SelectedFood, index: number }[] }, { group, food }, idx) => {
      if (!acc[group.date]) {
        acc[group.date] = [];
      }
      acc[group.date].push({ food, index: idx });
      return acc;
    }, {});

    return Object.entries(groupedData).map(([date, foods]) => ({ date, foods }));
  };

  // Define columns for the table
  const columns: Column[] = [
    { id: 'food', label: 'Food', minWidth: 160 },
    { id: 'times_selected', label: 'Times Selected', minWidth: 120 },
    { id: 'total_protein', label: 'Total Protein', minWidth: 120 },
    { id: 'total_carbs', label: 'Total Carbs', minWidth: 120 },
    { id: 'protein_category', label: 'Protein Category', minWidth: 140 },
    { id: 'carb_category', label: 'Carb Category', minWidth: 140 },
  ];

  // Calculate total count for client-side pagination
  const totalCount = selectedFoods?.reduce((acc, group) => acc + group.data.length, 0) || 0;

  if (!user?.restaurants[0]?.id) {
    return <div>No restaurant found</div>;
  }

  // Render table rows
  const renderRow = (item: { date: string; foods: { food: SelectedFood; index: number }[] }, _groupIndex: number) =>
    item.foods.map((foodItem, foodIndex) => (
      <TableRow
        key={`${item.date}-${foodItem.food.id}`}
        sx={{
          '& td': {
            borderBottom: foodIndex === item.foods.length - 1 ? '2px solid rgba(224, 224, 224, 1)' : undefined,
          },
          cursor: 'pointer',
          '&:hover': { bgcolor: 'rgba(145, 158, 171, 0.08)' }
        }}
        onClick={() => setSelectedFoodDetails({ id: foodItem.food.id, date: item.date })}
      >
        <TableCell>
          <Typography variant="body2">
            {foodItem.food.food}
          </Typography>
        </TableCell>
        <TableCell>{foodItem.food.times_selected}</TableCell>
        <TableCell>{foodItem.food.total_protein}</TableCell>
        <TableCell>{foodItem.food.total_carbs}</TableCell>
        <TableCell>{foodItem.food.protein_category}</TableCell>
        <TableCell>{foodItem.food.carb_category}</TableCell>
      </TableRow>
    ));

  // Render dialog content based on loading and error states
  const renderDialogContent = () => {
    if (selectedFoodLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (selectedFoodError) {
      return (
        <Box sx={{ p: 3, color: 'error.main' }}>
          <Typography>Error loading food details. Please try again.</Typography>
        </Box>
      );
    }

    if (!selectedFood) {
      return (
        <Box sx={{ p: 3 }}>
          <Typography>No details available for this food.</Typography>
        </Box>
      );
    }

    // Cast selectedFood to the schema type to ensure type safety
    const foodDetails = selectedFood as FoodDetail;

    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Selected {foodDetails.subscriptions.length} time(s)
        </Typography>

        {foodDetails.subscriptions && foodDetails.subscriptions.length > 0 && (
          <Box sx={{
            maxHeight: 600,
            overflow: 'auto',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider'
          }}>
            {foodDetails.subscriptions.map((subscription, index) => (
              <Box
                key={index}
                sx={{
                  p: 1.5,
                  borderBottom: index < foodDetails.subscriptions.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  '&:hover': { bgcolor: 'rgba(145, 158, 171, 0.08)' }
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontFamily: 'monospace',
                      mb: 0.5
                    }}
                  >
                    {subscription.phone_number}
                  </Typography>
                  <Typography variant="body2">
                    #{subscription.small_id} {subscription.name.split(' ')[0]}
                  </Typography>
                </Box>

                {subscription.restaurant_note && (
                  <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                    {subscription.restaurant_note}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <PermissionBasedGuard permissions={['partners:read:selectedfood']} hasContent>
      <Container maxWidth={false}>
        <CustomBreadcrumbs
          heading="Selected Food"
          sx={{ mb: 2 }}
          links={[{}]}
        />

        <Card>
          <TableToolbar
            filters={filters}
            onFilters={onFilters}
            onMultipleFilters={onMultipleFilters}
            onResetFilters={resetFilters}
            searchPlaceholder="Search food..."
          />

          <DataTable
            data={prepareData()}
            columns={columns}
            loading={selectedFoodsLoading}
            error={selectedFoodsError}
            page={page}
            rowsPerPage={rowsPerPage}
            count={totalCount}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            renderRow={renderRow}
          />
        </Card>

        {/* Food Details Dialog */}
        <Dialog
          open={!!selectedFoodDetails}
          onClose={handleCloseDetails}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              minHeight: '80vh',
              maxHeight: '90vh'
            }
          }}
        >
          <DialogTitle>
            {!selectedFoodLoading && selectedFood &&
              `${(selectedFood as FoodDetail).name_en} ${(selectedFood as FoodDetail).name_ar ? `(${(selectedFood as FoodDetail).name_ar})` : ''}`
            }
            <IconButton
              aria-label="close"
              onClick={handleCloseDetails}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
              }}
            >
              <Iconify icon="eva:close-fill" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {renderDialogContent()}
          </DialogContent>
        </Dialog>
      </Container>
    </PermissionBasedGuard>
  );
}
